/**
 * The standard top-of-page header: an accent icon + title + optional subtitle,
 * with an optional right-aligned `action` slot (a button, a count, etc.).
 *
 * Pass the page's nav icon (`NavIcons.tracker`, `NavIcons.skills`, …) so the
 * header icon matches the tab the user clicked — a small wayfinding cue. Used
 * by every top-level page so headers stay visually consistent (previously only
 * Quizzes had an icon).
 */

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { ICON_STROKE } from "@/shared/components/icons";

export interface PageHeaderProps {
  Icon?: LucideIcon;
  title: string;
  subtitle?: ReactNode;
  /** Right-aligned slot — a CTA button, a count, etc. */
  action?: ReactNode;
}

export function PageHeader({ Icon, title, subtitle, action }: PageHeaderProps) {
  return (
    <header className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {Icon && (
          <Icon
            size={22}
            strokeWidth={ICON_STROKE}
            className="text-accent shrink-0"
          />
        )}
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-text tracking-tight">{title}</h1>
          {subtitle && <p className="mt-0.5 text-sm text-text-muted">{subtitle}</p>}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </header>
  );
}
